import { type EventListQuery, GROUND_SPECS } from "@playarena/shared";
import type React from "react";

import { COUNTRIES } from "../../../utils/constants";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Slider } from "../../ui/slider";

interface EventFilterProps {
  filters: EventListQuery;
  onFiltersChange: (filters: EventListQuery) => void;
}

const EventFilter: React.FC<EventFilterProps> = ({ filters, onFiltersChange }) => {
  const handleFilterChange = (key: keyof EventListQuery, value: any) => {
    onFiltersChange({
      ...filters,
      [key]: value,
    });
  };

  const handlePriceRangeChange = (values: number[]) => {
    onFiltersChange({
      ...filters,
      minPrice: values[0] || (filters.minPrice === undefined ? 0 : filters.minPrice),
      maxPrice: values[1] || (filters.maxPrice === undefined ? 500 : filters.maxPrice),
    });
  };

  const clearFilters = () => {
    onFiltersChange({});
  };

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <h3 className='text-lg font-semibold'>Filters</h3>
        <Button variant='ghost' size='sm' onClick={clearFilters}>
          Clear All
        </Button>
      </div>

      <div className='grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4'>
        {/* Country */}
        <div className='space-y-2'>
          <Label>Country</Label>
          <Select
            value={filters.country || "all"}
            onValueChange={value =>
              handleFilterChange("country", value === "all" ? undefined : value)
            }
          >
            <SelectTrigger>
              <SelectValue placeholder='Select country' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All Countries</SelectItem>
              {COUNTRIES.map(country => (
                <SelectItem key={country.code} value={country.code}>
                  {country.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* City */}
        <div className='space-y-2'>
          <Label htmlFor='city'>City</Label>
          <Input
            id='city'
            placeholder='Enter city'
            value={filters.city || ""}
            onChange={e => handleFilterChange("city", e.target.value || undefined)}
          />
        </div>

        {/* Event Type */}
        <div className='space-y-2'>
          <Label>Event Type</Label>
          <Select
            value={filters.type || "all"}
            onValueChange={value => handleFilterChange("type", value === "all" ? undefined : value)}
          >
            <SelectTrigger>
              <SelectValue placeholder='Select type' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All Types</SelectItem>
              <SelectItem value={GROUND_SPECS.SOCCER}>Soccer</SelectItem>
              <SelectItem value={GROUND_SPECS.CRICKET}>Cricket</SelectItem>
              <SelectItem value={GROUND_SPECS.GENERAL}>General</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Price Range */}
        <div className='space-y-2'>
          <Label>Price Range</Label>
          <div className='px-2'>
            <Slider
              value={[filters.minPrice || 0, filters.maxPrice || 500]}
              onValueChange={handlePriceRangeChange}
              max={500}
              step={5}
              className='w-full'
            />
            <div className='mt-1 flex justify-between text-sm text-muted-foreground'>
              <span>${filters.minPrice || 0}</span>
              <span>${filters.maxPrice || 500}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Date Range */}
      <div className='space-y-2'>
        <Label>Date Range</Label>
        <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
          <div className='space-y-2'>
            <Label htmlFor='startDate' className='text-sm'>
              From
            </Label>
            <Input
              id='startDate'
              type='date'
              value={filters.startDate || ""}
              onChange={e => handleFilterChange("startDate", e.target.value)}
              min={new Date().toISOString().split("T")[0]}
            />
          </div>
          <div className='space-y-2'>
            <Label htmlFor='endDate' className='text-sm'>
              To
            </Label>
            <Input
              id='endDate'
              type='date'
              value={filters.endDate || ""}
              onChange={e => handleFilterChange("endDate", e.target.value)}
              min={filters.startDate || new Date().toISOString().split("T")[0]}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventFilter;
